/**
 * Client-side mirror of the server's command permission matrix.
 *
 * This exists so unauthorized controls can be visibly disabled instead of
 * inviting a rejection. It is *not* authorization: the server re-checks every
 * command, and a race (a policy change or a revoked controller status arriving
 * between render and click) legitimately produces a `permission-denied`
 * rejection that the UI shows. Capabilities come from the authenticated
 * `YouInfo` in the snapshot — never from a route name or local storage.
 *
 * | Command | Authorized when |
 * | --- | --- |
 * | `rename` | always (own display name) |
 * | `add-to-queue` | always (every guest keeps add permission) |
 * | `reorder-queue`, `remove-from-queue`, `clear-queue`, `retry-entry` | `queue_policy` |
 * | `play`, `pause`, `seek`, `skip-current`, `skip-sponsor-segment` | `playback_policy` |
 * | `send-chat`, `send-reaction` | always (every participant may talk and react) |
 * | `change-settings`, `grant-controller`, `revoke-controller` | host only |
 */

import type { ControlPolicy, RoomSettings, YouInfo } from '../generated';
import type { CommandKind } from './protocol';

export interface RoomPermissions {
  readonly isHost: boolean;
  readonly isController: boolean;
  readonly canControlPlayback: boolean;
  readonly canManageQueue: boolean;
  /** Host-only: settings and controller assignment. */
  readonly canAdminister: boolean;
}

function policyAllows(policy: ControlPolicy, you: YouInfo): boolean {
  // The host always acts regardless of policy (plan/02 item 4).
  if (you.capability === 'host') return true;
  return policy === 'everyone' || you.is_controller;
}

export function permissionsFor(you: YouInfo, settings: RoomSettings): RoomPermissions {
  const isHost = you.capability === 'host';
  return {
    isHost,
    isController: you.is_controller,
    canControlPlayback: policyAllows(settings.playback_policy, you),
    canManageQueue: policyAllows(settings.queue_policy, you),
    canAdminister: isHost,
  };
}

export function commandAllowed(kind: CommandKind, permissions: RoomPermissions): boolean {
  switch (kind) {
    case 'rename':
    case 'add-to-queue':
      return true;
    case 'reorder-queue':
    case 'remove-from-queue':
    case 'clear-queue':
    case 'retry-entry':
      return permissions.canManageQueue;
    case 'play':
    case 'pause':
    case 'seek':
    // Skipping moves the playhead off the current item, so it is transport
    // control rather than queue management (confirmed against the server).
    case 'skip-current':
    // A sponsor skip *is* a shared seek — one participant moves everyone's
    // playhead — so it follows the playback policy, not a separate rule
    // (plan/04 SponsorBlock item 3).
    case 'skip-sponsor-segment':
      return permissions.canControlPlayback;
    case 'send-chat':
    case 'send-reaction':
      // Social messages never move the timeline, so no policy gates them;
      // the server's rate limit, not a permission, bounds them.
      return true;
    case 'change-settings':
    case 'grant-controller':
    case 'revoke-controller':
      return permissions.canAdminister;
  }
}

/**
 * Why a control is disabled, for its `title`/`aria-describedby` text.
 *
 * `send-chat`/`send-reaction` are never denied here, so they return `null`
 * through the guard above; `skip-sponsor-segment` shares the policy wording
 * with the other playback commands because it *is* one of them.
 */
export function deniedReason(kind: CommandKind, permissions: RoomPermissions): string | null {
  if (commandAllowed(kind, permissions)) return null;
  if (kind === 'change-settings' || kind === 'grant-controller' || kind === 'revoke-controller') {
    return 'Only the host can do this.';
  }
  return 'The host limited this to controllers.';
}
